/**
 * Dividing a call into parts, and timing each one (task 2).
 *
 * The provider does not divide it for us. The reconnaissance of task 1 settled
 * that: 204 output deltas, not one `.done` or `.completed` among them, so there
 * is no completion marker to key on and a part has to be something we define.
 *
 * What it is here: **one utterance and the translation that follows it.** A
 * part opens on the first input delta after a silence, and closes once both
 * streams have been quiet long enough that the next thing said is a different
 * thought.
 *
 * When the room did not pay for source transcription there are no input
 * deltas, and a part opens on the translation instead. Those parts say so in
 * `boundary`, carry no `firstOutputMs` — there is no "first word said" to
 * measure from — and must not be averaged with the others. That is a judgement, not a measurement, so every part records which
 * rule ended it — `boundary` — and a reader who disagrees can filter on it.
 *
 * The measurements come off the provider's own clock. Every delta of both
 * kinds carries `elapsed_ms`, milliseconds since its session opened, and that
 * number is better than anything this page can produce: it does not move when
 * a tab is throttled, when the event loop is busy, or when a frame sat in a
 * buffer before `message` fired. The browser clock is used for exactly two
 * things — deciding *when* a silence has lasted long enough, which the
 * provider cannot tell us because silence produces no events, and stamping
 * `startedAt` so a row can be joined to a day.
 *
 * Nothing here holds a chunk back. The obvious alternative was to mark
 * `TranscriptChunk.final` on the last chunk of a part, which reads well and
 * cannot be done: a part is known to have ended only after the silence that
 * ends it, so every subtitle would have to be delayed by the gap before it
 * could be shown. A second of added latency on the one path section 10 cares
 * about, to label a line. The boundary is reported beside the subtitles
 * instead, and `final` stays honestly false.
 */

/** What one finished part says about itself. */
export interface CallPart {
  /** Monotonic within a session; a gap means a batch was lost in transit. */
  seq: number;
  /**
   * Which rule ended the part, and — by its prefix — what the part was built
   * from. `silence` and `session-end` are parts that began with somebody
   * speaking, which needs the room to have asked for source transcription.
   * The `output-` pair are parts delimited from the translation alone, which
   * is all there is when it did not.
   *
   * The two are different measurements and must not be averaged together.
   * That is the whole reason the difference is in the value rather than
   * implied: a reader who joins them anyway has to do it on purpose.
   */
  boundary: 'silence' | 'session-end' | 'output-silence' | 'output-session-end';
  /** Wall clock at the moment the part opened. For ordering and for the day. */
  startedAt: number;
  /**
   * From the first word of the utterance to the first word of its translation,
   * on the provider's clock. Absent when nothing came back — a part nobody
   * translated is worth keeping and worth being able to find.
   */
  firstOutputMs?: number;
  /** First delta to last, the translation's tail included. */
  durationMs: number;
  /** How many deltas of each kind made it up. Counts, never text. */
  inputDeltas: number;
  outputDeltas: number;
}

/**
 * How long everything has to be quiet before a part is considered over.
 *
 * Two forces set it. Too short and a pause for breath splits one sentence into
 * several parts, each with a nonsense latency. Too long and two separate
 * remarks are measured as one. It also has to outlast the translation's own
 * lag, or a part would close while the words answering it were still arriving
 * — the first measured value for that lag was 98 ms, so there is room.
 */
const DEFAULT_GAP_MS = 1500;

export interface PartsOptions {
  onPart: (part: CallPart) => void;
  /**
   * Whether this session will produce input transcripts at all — that is,
   * whether the room asked for source subtitles, which costs money.
   *
   * When it did, an utterance opens a part and `firstOutputMs` is the real
   * thing: first word said to first word translated. When it did not, there
   * are no input deltas, and a rule that only opens on them would produce no
   * parts whatsoever — which is what happened on the first staging call after
   * the checkbox shipped. So the translation delimits parts on its own, and
   * says so in `boundary`.
   */
  expectInput?: boolean;
  gapMs?: number;
  /** Test seam. Defaults to the wall clock. */
  now?: () => number;
}

export class CallParts {
  #onPart: (part: CallPart) => void;
  #gapMs: number;
  #now: () => number;
  #expectInput: boolean;

  #seq = 0;
  #open: {
    startedAt: number;
    startElapsed: number;
    lastElapsed: number;
    firstOutputElapsed: number | null;
    inputDeltas: number;
    outputDeltas: number;
  } | null = null;
  #timer: ReturnType<typeof setTimeout> | null = null;
  #done = false;

  constructor(options: PartsOptions) {
    this.#onPart = options.onPart;
    this.#gapMs = options.gapMs ?? DEFAULT_GAP_MS;
    this.#now = options.now ?? Date.now;
    this.#expectInput = options.expectInput ?? true;
  }

  /**
   * One transcript delta, as it arrived.
   *
   * `elapsedMs` is the provider's, not ours. A delta without one is still
   * counted — it says the stream is alive, which is what keeps the part open —
   * but it cannot move the measurements, so the last known value stands.
   */
  observe(direction: 'input' | 'output', elapsedMs: number | null): void {
    if (this.#done) return;

    if (!this.#open) {
      // With input transcripts, only speech starts a part: output arriving
      // with nothing open is the tail of a part that already closed, and
      // inventing a part for it would report a translation with no utterance
      // and a latency measured from nothing.
      //
      // Without them there is no speech to start from, and refusing to open
      // means measuring nothing at all — so the translation delimits itself.
      if (elapsedMs === null) return;
      if (direction !== 'input' && this.#expectInput) return;
      this.#open = {
        startedAt: this.#now(),
        startElapsed: elapsedMs,
        lastElapsed: elapsedMs,
        firstOutputElapsed: null,
        inputDeltas: 0,
        outputDeltas: 0,
      };
    }

    const part = this.#open;
    if (elapsedMs !== null && elapsedMs >= part.lastElapsed) part.lastElapsed = elapsedMs;
    if (direction === 'input') {
      part.inputDeltas += 1;
    } else {
      part.outputDeltas += 1;
      if (part.firstOutputElapsed === null && elapsedMs !== null) {
        part.firstOutputElapsed = elapsedMs;
      }
    }

    this.#arm();
  }

  /** The session is over; report whatever was still open. */
  finish(): void {
    if (this.#done) return;
    this.#done = true;
    this.#disarm();
    this.#close('session-end');
  }

  /** Test seam: fire the silence timer without waiting for it. */
  sweep(): void {
    this.#disarm();
    this.#close('silence');
  }

  get openParts(): number {
    return this.#open ? 1 : 0;
  }

  #arm(): void {
    this.#disarm();
    this.#timer = setTimeout(() => {
      this.#timer = null;
      this.#close('silence');
    }, this.#gapMs);
  }

  #disarm(): void {
    if (this.#timer !== null) clearTimeout(this.#timer);
    this.#timer = null;
  }

  #close(rule: 'silence' | 'session-end'): void {
    const part = this.#open;
    if (!part) return;
    this.#open = null;
    this.#seq += 1;

    // A part with no input delta in it was delimited by the translation alone,
    // whatever this session was configured to expect — so the label follows
    // what actually happened rather than what was asked for.
    const boundary: CallPart['boundary'] =
      part.inputDeltas > 0 ? rule : (`output-${rule}` as CallPart['boundary']);

    this.#onPart({
      seq: this.#seq,
      boundary,
      startedAt: part.startedAt,
      // Only when somebody was heard to speak. In a part the translation
      // delimited on its own, the first delta *is* the first output, so this
      // would compute a flat zero — the exact number that quietly drags an
      // average towards "instant" while measuring nothing.
      ...(part.inputDeltas > 0 &&
        part.firstOutputElapsed !== null && {
          // Clamped at zero: the two streams are timestamped by the same clock,
          // but nothing in the API promises the first translated word cannot
          // share a millisecond with the word that prompted it.
          firstOutputMs: Math.max(0, Math.round(part.firstOutputElapsed - part.startElapsed)),
        }),
      durationMs: Math.max(0, Math.round(part.lastElapsed - part.startElapsed)),
      inputDeltas: part.inputDeltas,
      outputDeltas: part.outputDeltas,
    });
  }
}
